/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Who has an insight on a dashboard. Built once per page from the users' dashboard
 * summaries; the editor asks it per insight and per category so a category counts a
 * user, and a dashboard, once even when several of its insights are on the same board.
 */

import type { DashboardSummary } from '../types'

export interface UsageCount {
  users: number
  dashboards: number
}

export interface UsageIndex {
  forSlug: (slug: string) => UsageCount
  forCategory: (slugs: readonly string[]) => UsageCount
  inUse: (slug: string) => boolean
  /** Every slug at least one user has on a dashboard. */
  slugs: readonly string[]
}

interface UsageUser {
  uid: string
  dashboards: DashboardSummary[]
}

interface Holders {
  users: Set<string>
  dashboards: Set<string>
}

export const buildUsage = (users: readonly UsageUser[]): UsageIndex => {
  const bySlug = new Map<string, Holders>()
  for (const user of users) {
    for (const dashboard of user.dashboards) {
      // Dashboards are keyed per user: two users' "Home" boards are two dashboards.
      const dashboardKey = `${user.uid}::${dashboard.slug}`
      for (const slug of dashboard.insights) {
        let holders = bySlug.get(slug)
        if (!holders) {
          holders = { users: new Set(), dashboards: new Set() }
          bySlug.set(slug, holders)
        }
        holders.users.add(user.uid)
        holders.dashboards.add(dashboardKey)
      }
    }
  }

  const count = (slugs: readonly string[]): UsageCount => {
    const holderUsers = new Set<string>()
    const holderDashboards = new Set<string>()
    for (const slug of slugs) {
      const holders = bySlug.get(slug)
      if (!holders) continue
      holders.users.forEach((uid) => holderUsers.add(uid))
      holders.dashboards.forEach((key) => holderDashboards.add(key))
    }
    return { users: holderUsers.size, dashboards: holderDashboards.size }
  }

  return {
    forSlug: (slug) => count([slug]),
    forCategory: (slugs) => count(slugs),
    inUse: (slug) => (bySlug.get(slug)?.users.size ?? 0) > 0,
    slugs: [...bySlug.keys()],
  }
}

export const EMPTY_USAGE: UsageIndex = buildUsage([])

const plural = (n: number, noun: string) => `${String(n)} ${noun}${n === 1 ? '' : 's'}`

/** "In use on 3 dashboards" — for a single user, where the user count is always 1. */
export const dashboardsLabel = (count: UsageCount): string => `In use on ${plural(count.dashboards, 'dashboard')}`

/** "In use by 2 users, 10 dashboards" — for a company or a selection of users. */
export const usersAndDashboardsLabel = (count: UsageCount): string =>
  `In use by ${plural(count.users, 'user')}, ${plural(count.dashboards, 'dashboard')}`
